import { useQuery } from '@tanstack/react-query';
import {
  evaluateFeatureFlag,
  getFeatureFlags,
  normalizeFeatureFlags,
  type FeatureFlagSnapshot,
} from '@fog-chess/core';

/**
 * useFeatureFlag - Mandate C (Feature Flags Dead-End Protection).
 *
 * Fetches the dynamic server feature flags via TanStack Query (server
 * state) and evaluates a single flag using the permissive default-merge
 * strategy from @fog-chess/core's `evaluateFeatureFlag`.
 *
 * Returns a small status object so screens can branch on `enabled`
 * (for routing) and `isLoading` (so a card never flashes "Coming Soon"
 * for a feature that is actually on but mid-fetch).
 *
 * Why this lives in apps/web (not core): the React hook needs
 * QueryClientProvider context, which is a web-app concern. The *evaluation*
 * logic is in core so it's unit tested in isolation.
 *
 * The query polls every 30s (matching the backend feature-flag L1 cache TTL)
 * so operational toggles propagate without a hard refresh.
 */
export interface UseFeatureFlagResult {
  /** Resolved boolean state of the flag. Conservative: defaults apply. */
  enabled: boolean;
  /** True while the first fetch is in flight (subsequent polls are silent). */
  isLoading: boolean;
  /** True if the latest fetch errored - screens may keep the previous value. */
  isError: boolean;
  /** Normalised snapshot so screens can read multiple flags cheaply. */
  snapshot: FeatureFlagSnapshot;
}

const FALLBACK_SNAPSHOT = normalizeFeatureFlags(null);

export function useFeatureFlag(flag: string): UseFeatureFlagResult {
  const query = useQuery({
    queryKey: ['feature-flags'],
    queryFn: getFeatureFlags,
    staleTime: 30_000,
    refetchInterval: 30_000,
    // Keep showing the last good snapshot while refetching in the background
    // so cards don't flicker. Network errors fall back to defaults.
    placeholderData: (prev) => prev,
  });

  const data = query.data ?? null;
  const snapshot = data ? normalizeFeatureFlags(data) : FALLBACK_SNAPSHOT;
  const enabled = evaluateFeatureFlag(data, flag);
  const isLoading =
    query.status === 'pending' && query.fetchStatus !== 'idle';
  const isError = query.status === 'error';

  return { enabled, isLoading, isError, snapshot };
}
